/**
 * Broadcast-native stats (PMA-BCAST-013). Same maths as the rows MatchAnalytics
 * builds; possession uses the exported `computePossessionSplit`. Shot, pass,
 * corner, offside, foul and card tallies are inline in the guarded component
 * (not exported), so they are mirrored here and held equal by the parity test
 * in NativeStatsPanel.test.tsx.
 */
import type { MatchEvent } from "../../../store/useMatchLogStore";
import type { Match } from "../../logger/types";
import { getCardSubjectId } from "../../logger/types";
import { computePossessionSplit } from "../../logger/utils";
import { sameTeamId } from "../../logger/utils/teamId";

export const NATIVE_STAT_KEYS = [
  "xg",
  "possession",
  "accurate-passes",
  "shots",
  "shots-on-target",
  "shots-off-target",
  "shots-blocked",
  "corners",
  "offsides",
  "fouls",
] as const;
export type NativeStatKey = (typeof NATIVE_STAT_KEYS)[number];

export interface NativeStatRow {
  key: NativeStatKey;
  /** analytics.* locale key + English fallback (same keys as the analytics page). */
  labelKey: string;
  labelFallback: string;
  /** Numeric values drive AnimatedNumber; `suffix` is appended ("%"). */
  home: number | null;
  away: number | null;
  suffix?: string;
  /** passes row: "<accurate> - <pct>%" */
  homeText?: string;
  awayText?: string;
  cards?: {
    home: { yellow: number; red: number };
    away: { yellow: number; red: number };
  };
}

const parseClock = (clock?: string) => {
  if (!clock) return 0;
  const [mm, rest] = clock.split(":");
  return Number(mm) * 60 + parseFloat(rest || "0");
};

const cardOrder = (
  l: { event: MatchEvent; index: number },
  r: { event: MatchEvent; index: number },
) => {
  const lp = Number(l.event.period || 0);
  const rp = Number(r.event.period || 0);
  if (lp !== rp) return lp - rp;
  const lt = Date.parse(l.event.timestamp || "");
  const rt = Date.parse(r.event.timestamp || "");
  if (Number.isFinite(lt) && Number.isFinite(rt) && lt !== rt) return lt - rt;
  const lc = parseClock(l.event.match_clock);
  const rc = parseClock(r.event.match_clock);
  if (lc !== rc) return lc - rc;
  return l.index - r.index;
};

export function netCardTotals(
  events: MatchEvent[],
  homeId: string,
  awayId: string,
) {
  const by = new Map<
    string,
    { teamId: string; yellow: number; red: number; suppress: number }
  >();
  events
    .map((event, index) => ({ event, index }))
    .filter(({ event }) => event.type === "Card" && !!getCardSubjectId(event))
    .sort(cardOrder)
    .forEach(({ event }) => {
      const id = getCardSubjectId(event)!;
      const cur = by.get(id) ?? {
        teamId: event.team_id,
        yellow: 0,
        red: 0,
        suppress: 0,
      };
      cur.teamId = event.team_id || cur.teamId;
      const type = String(event.data?.card_type || "").toLowerCase();
      if (type.includes("cancel")) {
        if (cur.red > 0 && cur.yellow >= 2) {
          cur.red -= 1;
          cur.yellow -= 1;
        } else if (cur.red > 0) cur.red -= 1;
        else if (cur.yellow > 0) cur.yellow -= 1;
      } else if (type.includes("yellow (second)")) {
        cur.yellow += 1;
        cur.red += 1;
        cur.suppress += 1;
      } else if (type.includes("yellow")) cur.yellow += 1;
      else if (type.includes("red")) {
        if (cur.suppress > 0) cur.suppress -= 1;
        else cur.red += 1;
      }
      by.set(id, cur);
    });
  const totals = { home: { yellow: 0, red: 0 }, away: { yellow: 0, red: 0 } };
  by.forEach(({ teamId, yellow, red }) => {
    const side = sameTeamId(teamId, homeId)
      ? "home"
      : sameTeamId(teamId, awayId)
        ? "away"
        : null;
    if (side) {
      totals[side].yellow += yellow;
      totals[side].red += red;
    }
  });
  return totals;
}

export function computeNativeStats(
  match: Match,
  events: MatchEvent[],
  xg?: { home: number; away: number } | null,
): NativeStatRow[] {
  const homeId = match.home_team.id;
  const awayId = match.away_team.id;
  const h = events.filter((e) => sameTeamId(e.team_id, homeId));
  const a = events.filter((e) => sameTeamId(e.team_id, awayId));
  const count = (es: MatchEvent[], type: string) =>
    es.filter((e) => e.type === type).length;
  const shotOutcome = (es: MatchEvent[], outcomes: string[]) =>
    es.filter(
      (e) =>
        e.type === "Shot" &&
        outcomes.includes(String(e.data?.outcome || "")) &&
        !e.data?.own_goal,
    ).length;
  const passes = (es: MatchEvent[]) => ({
    total: count(es, "Pass"),
    accurate: es.filter(
      (e) => e.type === "Pass" && e.data?.outcome === "Complete",
    ).length,
  });
  const pct = (p: { total: number; accurate: number }) =>
    p.total > 0 ? Math.round((p.accurate / p.total) * 100) : 0;
  const shots = (es: MatchEvent[]) =>
    es.filter((e) => e.type === "Shot" && !e.data?.own_goal).length;
  const corners = (es: MatchEvent[]) =>
    es.filter(
      (e) => e.type === "SetPiece" && e.data?.set_piece_type === "Corner",
    ).length;
  const offsides = (es: MatchEvent[]) =>
    count(es, "Offside") +
    es.filter((e) => e.type === "Pass" && e.data?.outcome === "Pass Offside")
      .length;

  const hp = passes(h);
  const ap = passes(a);
  const { homePossession, awayPossession } = computePossessionSplit({
    homeWeight: hp.total,
    awayWeight: ap.total,
    contestedWeight: count(h, "Duel") + count(a, "Duel"),
  });
  const cards = netCardTotals(events, homeId, awayId);

  const row = (
    key: NativeStatKey,
    labelKey: string,
    labelFallback: string,
    home: number | null,
    away: number | null,
    extra: Partial<NativeStatRow> = {},
  ): NativeStatRow => ({ key, labelKey, labelFallback, home, away, ...extra });

  return [
    row(
      "xg",
      "analytics.xg",
      "Expected Goals (xG)",
      xg ? xg.home : null,
      xg ? xg.away : null,
    ),
    row(
      "possession",
      "analytics.possession",
      "Possession",
      homePossession,
      awayPossession,
      { suffix: "%" },
    ),
    row(
      "accurate-passes",
      "analytics.accuratePasses",
      "Passes (Total/Accurate)",
      hp.accurate,
      ap.accurate,
      {
        homeText: `${hp.accurate} - ${pct(hp)}%`,
        awayText: `${ap.accurate} - ${pct(ap)}%`,
      },
    ),
    row("shots", "analytics.totalShots", "Total shots", shots(h), shots(a)),
    row(
      "shots-on-target",
      "analytics.shotsOnTarget",
      "Shots on Target",
      shotOutcome(h, ["OnTarget", "Saved", "Goal"]),
      shotOutcome(a, ["OnTarget", "Saved", "Goal"]),
    ),
    row(
      "shots-off-target",
      "analytics.shotsOffTarget",
      "Shots off Target",
      shotOutcome(h, ["OffTarget", "Post"]),
      shotOutcome(a, ["OffTarget", "Post"]),
    ),
    row(
      "shots-blocked",
      "analytics.shotsBlocked",
      "Blocked shots",
      shotOutcome(h, ["Blocked"]),
      shotOutcome(a, ["Blocked"]),
    ),
    row("corners", "analytics.corners", "Corners", corners(h), corners(a)),
    row("offsides", "analytics.offsides", "Offsides", offsides(h), offsides(a)),
    row(
      "fouls",
      "analytics.fouls",
      "Fouls",
      count(h, "FoulCommitted"),
      count(a, "FoulCommitted"),
      { cards },
    ),
  ];
}
